// src/Views/BuildDetailPage.js — Reusable build detail page template
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const buildData = {
  'gears-of-destiny': {
    title: 'Gears of Destiny',
    status: 'In Progress',
    domain: 'Board Game Design',
    seoDescription: 'A cooperative board game where the map is a mechanical system — counter-rotating rings, spinning platforms, and doors on timing cycles. Currently in playtesting.',
    problem: {
      heading: 'The Problem',
      paragraphs: [
        'Board games are static. The map never changes. Cooperative games especially suffer — once you solve the puzzle, replayability drops. Players memorize the optimal path and the challenge disappears.',
        'What if the map itself was a mechanical system with moving parts?',
      ],
    },
    systemDesign: {
      heading: 'System Design',
      paragraphs: [
        'Counter-rotating rings create a map that shifts every turn. Spinning platforms change which paths are connected. Doors open and close on timing cycles. Players must think in systems — not just "where do I go" but "where will the board BE when I get there."',
        'The game is designed as interlocking subsystems: ring rotation, platform alignment, door timing, and player movement all interact. Each subsystem is simple. The complexity emerges from their combination — the same design principle behind every build I make.',
      ],
    },
    outcome: {
      heading: 'Current Status',
      items: [
        { status: 'done', text: 'Core mechanical system designed' },
        { status: 'done', text: 'Playtest prototypes built' },
        { status: 'done', text: 'Rules written' },
        { status: 'done', text: 'Art direction established' },
        { status: 'wip', text: 'Currently in iterative playtesting' },
      ],
    },
  },
  'workaround': {
    title: 'WorkAround',
    status: 'Active',
    domain: 'Browser Automation',
    seoDescription: 'A Chrome extension that records browser workflows as semantic instructions for AI agents. No code, no APIs — you do it once, agents do it from there.',
    problem: {
      heading: 'The Problem',
      paragraphs: [
        'AI browser agents — Browser-Use, Playwright, Claude Computer Use — can execute tasks, but they need instructions. Today, writing those instructions means code: selectors, navigation logic, error handling. Non-technical operators can\'t use them.',
        'What if you could just show the browser what to do?',
      ],
    },
    systemDesign: {
      heading: 'System Design',
      paragraphs: [
        'WorkAround is a Chrome extension (MV3) that records browser workflows as semantic instructions. It captures not just what was clicked, but WHY — intent, context, and multiple fallback selectors so AI agents can adapt when websites change.',
        'The recording pipeline normalizes raw browser events into a structured workflow graph. Export formats include SKILL.md (AI-readable) and JSON.',
        'Architecture: content script captures DOM events → background service worker normalizes → sidepanel React app displays workflow → export pipeline generates instructions.',
      ],
    },
    outcome: {
      heading: 'Current Status',
      items: [
        { status: 'done', text: 'Chrome extension (MV3) with recording and workflow execution' },
        { status: 'done', text: 'Semantic selector generation with fallback strategies' },
        { status: 'done', text: 'Sidepanel UI built in React/TypeScript — list and canvas views' },
        { status: 'done', text: 'SKILL.md and JSON export formats' },
        { status: 'wip', text: 'Distribution pipeline — WorkAround → LinkedIn posting via AI agent' },
        { status: 'wip', text: 'Design system tokenization' },
        { status: 'wip', text: 'Integration with the Content Engine' },
      ],
    },
    landingPageLink: '/workaround',
  },
};

const styles = {
  page: {
    backgroundColor: 'var(--color-bg-canvas)',
    color: 'var(--color-text-on-dark)',
    minHeight: '100vh',
  },
  hero: {
    padding: 'calc(var(--space-16) + 60px) var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  badge: {
    display: 'inline-block',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 10px',
    marginBottom: 'var(--space-3)',
    letterSpacing: 'var(--letter-spacing-label)',
    textTransform: 'uppercase',
  },
  domain: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-on-dark-muted)',
    textTransform: 'uppercase',
    letterSpacing: 'var(--letter-spacing-label)',
    marginBottom: 'var(--space-4)',
  },
  title: {
    fontSize: 'var(--font-size-4xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-2)',
    lineHeight: 'var(--line-height-tight)',
  },
  section: {
    padding: '0 var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  paragraph: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-on-dark-body)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-4)',
  },
  statusWrapper: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 var(--space-6) var(--space-12)',
  },
  statusSection: {
    padding: 'var(--space-8) var(--space-6)',
    backgroundColor: 'var(--color-bg-surface)',
    borderRadius: 'var(--radius-lg)',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '7px',
    flexShrink: 0,
  },
  statusText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  backLink: {
    display: 'inline-block',
    padding: 'var(--space-8) var(--space-6) var(--space-16)',
    maxWidth: '800px',
    margin: '0 auto',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-accent-link)',
    textDecoration: 'none',
  },
  landingLink: {
    display: 'inline-block',
    marginTop: 'var(--space-6)',
    padding: 'var(--space-3) var(--space-8)',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-dark)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    transition: 'background-color var(--transition-base), box-shadow var(--transition-base)',
  },
  notFound: {
    padding: 'calc(var(--space-16) + 60px) var(--space-6)',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
  },
};

const statusDotColor = {
  done: 'var(--color-success)',
  wip: 'var(--color-warning)',
  planned: 'var(--color-text-on-dark-muted)',
};

const statusBadgeColor = {
  'Active': 'var(--color-success)',
  'In Progress': 'var(--color-warning)',
  'Shipped': 'var(--color-success)',
};

const BuildDetailPage = () => {
  const { slug } = useParams();
  const build = buildData[slug];

  if (!build) {
    return (
      <div style={styles.page}>
        <div style={styles.notFound}>
          <h1 style={styles.title}>Build not found</h1>
          <p style={styles.paragraph}>
            This build doesn't have a detail page yet.
          </p>
          <Link to="/builds" style={{ color: 'var(--color-accent-link)', textDecoration: 'none' }}>
            ← Back to Builds
          </Link>
        </div>
      </div>
    );
  }

  const badgeColor = statusBadgeColor[build.status] || 'var(--color-text-on-dark-muted)';

  return (
    <div style={styles.page}>
      <Helmet>
        <title>{build.title} — Solomon Foshko | Design by Dialogue</title>
        <meta name="description" content={build.seoDescription} />
      </Helmet>

      {/* Hero */}
      <div style={styles.hero}>
        <span style={{ ...styles.badge, color: badgeColor, border: `1px solid ${badgeColor}` }}>
          {build.status}
        </span>
        <h1 style={styles.title}>{build.title}</h1>
        <div style={styles.domain}>{build.domain}</div>
      </div>

      {/* Problem */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{build.problem.heading}</h2>
        {build.problem.paragraphs.map((p, i) => (
          <p key={i} style={styles.paragraph}>{p}</p>
        ))}
      </div>

      {/* System Design */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{build.systemDesign.heading}</h2>
        {build.systemDesign.paragraphs.map((p, i) => (
          <p key={i} style={styles.paragraph}>{p}</p>
        ))}
      </div>

      {/* Outcome / Status */}
      <div style={styles.statusWrapper}>
        <div style={styles.statusSection}>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 'var(--space-4)' }}>
            {build.outcome.heading}
          </h2>
          {build.outcome.items.map((item, i) => (
            <div key={i} style={styles.statusItem}>
              <div style={{ ...styles.statusDot, backgroundColor: statusDotColor[item.status] }} />
              <p style={styles.statusText}>{item.text}</p>
            </div>
          ))}
          {build.landingPageLink && (
            <Link
              to={build.landingPageLink}
              style={styles.landingLink}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary-dark)';
                e.target.style.boxShadow = 'var(--shadow-dark-button)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary)';
                e.target.style.boxShadow = 'none';
              }}
            >
              View landing page →
            </Link>
          )}
        </div>
      </div>

      {/* Back link */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Link to="/builds" style={styles.backLink}>
          ← Back to Builds
        </Link>
      </div>
    </div>
  );
};

export default BuildDetailPage;
